import { Card, CardContent, Divider, Grid, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcContainer, MprcSpinnerIndicator } from 'components';
import { MprcPaper } from 'components/MprcPaper';
import { EnrollStatus, LearnPathEnrollmentPhasesResponse } from 'generated/CoreApi_v1';
import TzUtils from 'lib/TzUtils';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RenderCardIcon } from 'views/general/Cards/Common';
import { useEnrollmentViewContext } from 'views/lms/student/EnrollmentView';
import { CardDtoToLearnPathCardRibbon } from 'views/lms/student/EnrollmentView/LearnPath/CardDtoToLearnPathCardRibbon';

export const LpEnrollmentViewSteps: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { enrollment, onReload } = useEnrollmentViewContext();

    const [viewData, setViewData] = useState<LearnPathEnrollmentPhasesResponse>();
    const [loading, setLoading] = useState<boolean>(true);

    const loadData = () => {
        api.v1.enrollment.getLearnPathEnrollmentPhases({
            enrollmentId: enrollment.isPreview ? null : enrollment.enrollmentId,
            classroomId: enrollment.isPreview ? enrollment.classroomId : null,
            browserTimeZoneId: TzUtils.browserTimezone()
        })
            .then(response => setViewData(response.data))
            .catch(e => alert(e))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadData();
    }, [enrollment]);

    const phases = viewData?.items.filter(p => p.cards.length > 0) ?? [];
    const lastPhaseIndex = phases.length - 1;
    const currentCardStatus = viewData?.current?.card.data["enrollStatus"] as EnrollStatus | null;

    return (
        <MprcContainer>
            {viewData &&
                <>
                    {viewData.current &&
                        <MprcContainer>
                            <MprcPaper
                                className={viewData.current.card.disabled ? AppGlobalClassKeys.filterDisabled : undefined}
                                borderColor="warning"
                                elevation={4}
                                sx={theme => ({ bgcolor: theme.palette.notice.light })}
                            >
                                <MprcContainer>
                                    <Typography variant="h3">{currentCardStatus == null || currentCardStatus == EnrollStatus.NotStarted ? t("Student.EnrollmentView.LearnPath.CurrentComponentNotStartedTitle") : t("Student.EnrollmentView.LearnPath.CurrentComponentTitle")}</Typography>
                                </MprcContainer>
                                {CardDtoToLearnPathCardRibbon([viewData.current.card], { onCarouselReload: onReload })}
                            </MprcPaper>
                        </MprcContainer>
                    }
                    <Card>
                        <CardContent>
                            {loading && <MprcSpinnerIndicator></MprcSpinnerIndicator>}
                            {phases.map((phase, phaseIndex) =>
                                <MprcContainer key={phase.phaseId} margin={phaseIndex === lastPhaseIndex ? "none" : "large"}>
                                    <MprcContainer margin="normal">
                                        <Typography variant="h3">{phase.title}</Typography>
                                        <Typography variant="caption">
                                            {t("Student.EnrollmentView.LearnPath.ModuleText", { count: phase.cards.length })}
                                            {(phase.minCredits != null || phase.maxCredits != null) &&
                                                <>
                                                    <span>&nbsp;&bull;&nbsp;</span>
                                                    {t("Student.EnrollmentView.LearnPath.CreditsText")}
                                                    <span>&nbsp;</span>
                                                    {phase.minCredits != null && t("Student.EnrollmentView.LearnPath.MinCreditsText", { count: phase.minCredits })}
                                                    {phase.minCredits != null && phase.maxCredits != null && <span>&nbsp;/&nbsp;</span>}
                                                    {phase.maxCredits != null && t("Student.EnrollmentView.LearnPath.MaxCreditsText", { count: phase.maxCredits })}
                                                </>
                                            }
                                        </Typography>
                                    </MprcContainer>
                                    <Stack alignItems="start">
                                        {phase.cards.map((item, cardIndex) => {
                                            const lastCardIndex = phase.cards.length - 1;
                                            return (
                                                <React.Fragment key={item.card.uniqueId}>
                                                    <Grid
                                                        container
                                                        spacing={2}
                                                        alignItems="flex-start"
                                                        justifyContent="flex-start"
                                                        wrap="nowrap"
                                                        width={"100%"}
                                                        className={item.card.disabled ? AppGlobalClassKeys.filterDisabled : undefined}
                                                    >
                                                        <Grid>
                                                            <RenderCardIcon
                                                                icon={item.stateIndicator}
                                                                fontSize={22}
                                                                fixedWidth
                                                            />
                                                        </Grid>
                                                        <Grid size="grow">
                                                            {CardDtoToLearnPathCardRibbon([item.card], { onCarouselReload: onReload })}
                                                        </Grid>
                                                    </Grid>
                                                    {(phaseIndex != lastPhaseIndex || (phaseIndex == lastPhaseIndex && cardIndex != lastCardIndex)) &&
                                                        <Divider flexItem sx={theme => ({ borderBottomWidth: 4, my: 2, borderColor: theme.palette.paper.dark })} />
                                                    }
                                                </React.Fragment>
                                            );
                                        })}
                                    </Stack>
                                </MprcContainer>
                            )}
                        </CardContent>
                    </Card>
                </>
            }
        </MprcContainer>
    );
};
